import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGuidedTour } from '../contexts/GuidedTourContext';
import './GuidedTour.css';

// Constants
const NAVIGATION_DELAY_MS = 300; // Delay before highlighting elements after navigation

const GuidedTour: React.FC = () => {
  const { isActive, currentStepIndex, exitTour, nextStep, previousStep, getCurrentStep, tourSteps } = useGuidedTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlightedElement, setHighlightedElement] = useState<DOMRect | null>(null);
  const [dialoguePosition, setDialoguePosition] = useState<{ top: number; left: number } | null>(null);
  const currentStep = getCurrentStep();
  const previousStepRef = useRef(currentStep);

  // Block all keyboard and mouse events when tour is active
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Allow keyboard events only if they target the dialogue or test buttons
      const target = e.target as HTMLElement;
      const isDialogueTarget = target.closest('.guided-tour-dialogue');
      const isTestButton = target.getAttribute('data-testid')?.includes('tour') ||
                           target.getAttribute('data-testid')?.includes('step');

      // Block all keyboard events except those targeting the dialogue or test buttons
      if (!isDialogueTarget && !isTestButton) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isDialogueTarget = target.closest('.guided-tour-dialogue');
      const isTestButton = target.getAttribute('data-testid')?.includes('tour') ||
                           target.getAttribute('data-testid')?.includes('step');

      if (!isDialogueTarget && !isTestButton) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isDialogueTarget = target.closest('.guided-tour-dialogue');
      const isTestButton = target.getAttribute('data-testid')?.includes('tour') ||
                           target.getAttribute('data-testid')?.includes('step');

      if (!isDialogueTarget && !isTestButton) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isDialogueTarget = target.closest('.guided-tour-dialogue');
      const isTestButton = target.getAttribute('data-testid')?.includes('tour') ||
                           target.getAttribute('data-testid')?.includes('step');

      // Block all clicks except those on the dialogue or test buttons
      if (!isDialogueTarget && !isTestButton) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isDialogueTarget = target.closest('.guided-tour-dialogue');
      const isTestButton = target.getAttribute('data-testid')?.includes('tour') ||
                           target.getAttribute('data-testid')?.includes('step');

      if (!isDialogueTarget && !isTestButton) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('keypress', handleKeyPress, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mousedown', handleMouseDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('keypress', handleKeyPress, true);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, [isActive]);

  // Navigate to the correct page when step changes
  useEffect(() => {
    if (!isActive || !currentStep) return;

    // Only navigate if the page actually changed
    if (previousStepRef.current?.page !== currentStep.page) {
      if (location.pathname !== currentStep.page) {
        navigate(currentStep.page);
      }
    }

    previousStepRef.current = currentStep;
  }, [currentStep, isActive, navigate, location.pathname]);

  // Highlight the target element and position dialogue
  useEffect(() => {
    if (!isActive || !currentStep) {
      setHighlightedElement(null);
      setDialoguePosition(null);
      return;
    }

    // Wait for navigation to complete before trying to find element
    const timer = setTimeout(() => {
      if (currentStep.elementSelector) {
        const element = document.querySelector(currentStep.elementSelector);
        if (element) {
          // Add hover effect to navigation links to make them appear active
          if (element.matches('[href]')) {
            element.classList.add('tour-highlight-active');
          }

          const rect = element.getBoundingClientRect();
          setHighlightedElement(rect);

          // Calculate dialogue position based on element position and step position preference
          const position = currentStep.position || 'bottom';
          let top = 0;
          let left = 0;

          const dialogueWidth = 320;
          const dialogueHeight = 250;
          const padding = 20;

          switch (position) {
            case 'top':
              top = rect.top - dialogueHeight - padding;
              left = rect.left + rect.width / 2 - dialogueWidth / 2;
              break;
            case 'bottom':
              top = rect.bottom + padding;
              left = rect.left + rect.width / 2 - dialogueWidth / 2;
              break;
            case 'left':
              top = rect.top + rect.height / 2 - dialogueHeight / 2;
              left = rect.left - dialogueWidth - padding;
              break;
            case 'right':
              top = rect.top + rect.height / 2 - dialogueHeight / 2;
              left = rect.right + padding;
              break;
          }

          // Check if dialogue overlaps with highlighted element
          const dialogueRect = {
            top,
            left,
            right: left + dialogueWidth,
            bottom: top + dialogueHeight,
          };

          const elementRect = {
            top: rect.top - 8,
            left: rect.left - 8,
            right: rect.right + 8,
            bottom: rect.bottom + 8,
          };

          // Detect overlap
          const overlaps = !(
            dialogueRect.right < elementRect.left ||
            dialogueRect.left > elementRect.right ||
            dialogueRect.bottom < elementRect.top ||
            dialogueRect.top > elementRect.bottom
          );

          // If overlapping, try alternative positions
          if (overlaps) {
            const positions: Array<'top' | 'bottom' | 'left' | 'right'> = ['bottom', 'top', 'right', 'left'];

            for (const tryPosition of positions) {
              let tryTop = 0;
              let tryLeft = 0;

              switch (tryPosition) {
                case 'top':
                  tryTop = rect.top - dialogueHeight - padding;
                  tryLeft = rect.left + rect.width / 2 - dialogueWidth / 2;
                  break;
                case 'bottom':
                  tryTop = rect.bottom + padding;
                  tryLeft = rect.left + rect.width / 2 - dialogueWidth / 2;
                  break;
                case 'left':
                  tryTop = rect.top + rect.height / 2 - dialogueHeight / 2;
                  tryLeft = rect.left - dialogueWidth - padding;
                  break;
                case 'right':
                  tryTop = rect.top + rect.height / 2 - dialogueHeight / 2;
                  tryLeft = rect.right + padding;
                  break;
              }

              const tryDialogueRect = {
                top: tryTop,
                left: tryLeft,
                right: tryLeft + dialogueWidth,
                bottom: tryTop + dialogueHeight,
              };

              const tryOverlaps = !(
                tryDialogueRect.right < elementRect.left ||
                tryDialogueRect.left > elementRect.right ||
                tryDialogueRect.bottom < elementRect.top ||
                tryDialogueRect.top > elementRect.bottom
              );

              // Also check if position is within viewport
              const inViewport =
                tryTop >= padding &&
                tryLeft >= padding &&
                tryTop + dialogueHeight <= window.innerHeight - padding &&
                tryLeft + dialogueWidth <= window.innerWidth - padding;

              if (!tryOverlaps && inViewport) {
                top = tryTop;
                left = tryLeft;
                break;
              }
            }
          }

          // Ensure dialogue stays within viewport
          if (left + dialogueWidth > window.innerWidth) {
            left = window.innerWidth - dialogueWidth - padding;
          }
          if (left < padding) {
            left = padding;
          }
          if (top + dialogueHeight > window.innerHeight) {
            top = window.innerHeight - dialogueHeight - padding;
          }
          if (top < padding) {
            top = padding;
          }

          setDialoguePosition({ top, left });
        } else {
          // Element not found, position dialogue at bottom center
          setHighlightedElement(null);
          const dialogueHeight = 250;
          const dialogueWidth = 320;
          const padding = 20;
          setDialoguePosition({
            top: window.innerHeight - dialogueHeight - padding,
            left: window.innerWidth / 2 - dialogueWidth / 2,
          });
        }
      } else {
        // No element to highlight (welcome/intro steps), show dialogue at bottom center
        setHighlightedElement(null);
        const dialogueHeight = 250;
        const dialogueWidth = 320;
        const padding = 20;
        setDialoguePosition({
          top: window.innerHeight - dialogueHeight - padding,
          left: window.innerWidth / 2 - dialogueWidth / 2,
        });
      }
    }, NAVIGATION_DELAY_MS); // Wait for navigation and rendering

    return () => {
      clearTimeout(timer);
      // Remove the active class from any previously highlighted elements
      const activeElements = document.querySelectorAll('.tour-highlight-active');
      activeElements.forEach(el => el.classList.remove('tour-highlight-active'));
    };
  }, [currentStep, isActive, currentStepIndex]);

  // Handle window resize
  useEffect(() => {
    if (!isActive) return;

    const handleResize = () => {
      // Recalculate positions on resize
      if (currentStep?.elementSelector) {
        const element = document.querySelector(currentStep.elementSelector);
        if (element) {
          const rect = element.getBoundingClientRect();
          setHighlightedElement(rect);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, currentStep]);

  if (!isActive || !currentStep) {
    return null;
  }

  return (
    <div className="guided-tour-container">
      {/* Gray overlay with cutout for highlighted element using positioned divs */}
      {highlightedElement ? (
        <>
          {/* Top overlay */}
          <div
            className="tour-overlay-section"
            style={{
              top: 0,
              left: 0,
              width: '100%',
              height: `${Math.max(0, highlightedElement.top - 8)}px`,
            }}
          />
          {/* Left overlay */}
          <div
            className="tour-overlay-section"
            style={{
              top: `${highlightedElement.top - 8}px`,
              left: 0,
              width: `${Math.max(0, highlightedElement.left - 8)}px`,
              height: `${highlightedElement.height + 16}px`,
            }}
          />
          {/* Right overlay */}
          <div
            className="tour-overlay-section"
            style={{
              top: `${highlightedElement.top - 8}px`,
              left: `${highlightedElement.right + 8}px`,
              width: `${Math.max(0, window.innerWidth - (highlightedElement.right + 8))}px`,
              height: `${highlightedElement.height + 16}px`,
            }}
          />
          {/* Bottom overlay */}
          <div
            className="tour-overlay-section"
            style={{
              top: `${highlightedElement.bottom + 8}px`,
              left: 0,
              width: '100%',
              height: `${Math.max(0, window.innerHeight - (highlightedElement.bottom + 8))}px`,
            }}
          />
        </>
      ) : (
        /* Full screen overlay when no element is highlighted */
        <div className="tour-overlay-section" style={{ top: 0, left: 0, width: '100%', height: '100%' }} />
      )}

      {/* Highlight border around the element */}
      {highlightedElement && (
        <>
          {/* Light background to make element visible */}
          <div
            className="highlight-background"
            style={{
              top: `${highlightedElement.top - 8}px`,
              left: `${highlightedElement.left - 8}px`,
              width: `${highlightedElement.width + 16}px`,
              height: `${highlightedElement.height + 16}px`,
            }}
          />
          {/* Border highlight */}
          <div
            className="highlight-border"
            style={{
              top: `${highlightedElement.top - 8}px`,
              left: `${highlightedElement.left - 8}px`,
              width: `${highlightedElement.width + 16}px`,
              height: `${highlightedElement.height + 16}px`,
            }}
          />
        </>
      )}

      {/* Dialogue box with writer character */}
      {dialoguePosition && (
        <div
          className="guided-tour-dialogue"
          style={{
            top: `${dialoguePosition.top}px`,
            left: `${dialoguePosition.left}px`,
          }}
        >
          <div className="dialogue-content">
            <h3>{currentStep.title}</h3>
            <p>{currentStep.description}</p>
          </div>

          <div className="dialogue-controls">
            <button
              className="tour-button secondary"
              onClick={previousStep}
              disabled={currentStepIndex === 0}
            >
              ← Prev
            </button>
            <span className="step-indicator">
              {currentStepIndex + 1} / {tourSteps.length}
            </span>
            <button
              className="tour-button primary"
              onClick={nextStep}
            >
              {currentStepIndex === tourSteps.length - 1 ? 'Finish' : 'Next →'}
            </button>
            <button
              className="exit-tour-button"
              onClick={exitTour}
              title="Exit Tour"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedTour;
